import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { useDispatch, useSelector, Provider } from "react-redux";
import "./src/index.css";
import { getAuthKeys } from "./src/models/login/login.actions";
import App from "./src/App";
import reportWebVitals from "./src/reportWebVitals";
import { ViewportProvider } from "./src/hooks/useViewPort";
import { store } from "./src/redux/store";
import setupInterceptors from "./src/interceptor";
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";

const initializeDatadog = (clientToken: string) => {
  const resolvedToken = clientToken || "";
  if (!resolvedToken) {
    return;
  }

  datadogRum.init({
    applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID ?? "",
    clientToken: resolvedToken,
    site: process.env.REACT_APP_DATADOG_SITE,
    service: process.env.REACT_APP_DATADOG_SERVICE,
    env: process.env.REACT_APP_DATADOG_ENV,
    version: "1.0.0",
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
  });
  datadogLogs.init({
    clientToken: resolvedToken,
    site: process.env.REACT_APP_DATADOG_SITE,
    service: process.env.REACT_APP_DATADOG_SERVICE,
    env: process.env.REACT_APP_DATADOG_ENV,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: "all",
  });
};

const AppBootstrap = () => {
  const dispatch = useDispatch();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {

    getAuthKeys(dispatch, (_isSuccess: boolean, _message: string | null, responseData: any) => {
      const authPayload = responseData?.data && typeof responseData.data === "object" && !Array.isArray(responseData.data) ? responseData.data : responseData;
      const resolvedToken = authPayload?.REACT_APP_DATADOG_CLIENT_TOKEN ?? "";
      if (resolvedToken) {
        initializeDatadog(resolvedToken);
      }
      setIsReady(true);
    });
  }, [dispatch]);

  if (!isReady) {
    return null;
  }

  return (
    <BrowserRouter>
      <ViewportProvider>
        <App />
      </ViewportProvider>
    </BrowserRouter>
  );
};

const disableReactDevtoolsHook = () => {
  if (process.env.NODE_ENV !== "development") {
    return;
  }

  const hook = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__;
  if (hook) {
    try {
      hook.inject = () => {};
      hook.onCommitFiberRoot = () => {};
      hook.onCommitFiberUnmount = () => {};
    } catch (error) {
      // Ignore devtools hook issues in browser extensions.
    }
  }
};

disableReactDevtoolsHook();

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
// Configure Store
setupInterceptors(store);
root.render(
  <Provider store={store}>
    <AppBootstrap />
  </Provider>
);
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
